import {
    memo,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Dropdown, type DropdownProps, type MenuProps } from 'antd';

import { useUpdateProfileDataMutation } from '@/features/profile';

import { useViewerId, ViewerAvatar } from '@/entities/viewer';

import { invalidateSession } from '@/shared/api';
import type { Language } from '@/shared/constants/language';
import * as route from '@/shared/constants/router';
import { useLanguage, useTour } from '@/shared/lib/hooks';
import { logError } from '@/shared/lib/logging';
import { useAppDispatch } from '@/shared/lib/store';
import { LanguageSwitcher } from '@/shared/ui/language-switcher';
import { ThemeSwitcher } from '@/shared/ui/theme-switcher';
import { TourTriggerMenuItem } from '@/shared/ui/tour-trigger-menu-item';

import packageJson from '../../../../../package.json';
import { HeaderMenuContent } from '../header-menu-content/header-menu-content';

import cls from './header-menu-trigger.module.css';

export const HeaderMenuTrigger = memo(() => {
    const dispatch = useAppDispatch();

    const [isOpened, setIsOpened] = useState<boolean>(false);

    const { t } = useTranslation();

    const [language] = useLanguage();
    const viewerId = useViewerId();
    const [updateProfileData] = useUpdateProfileDataMutation();
    const { steps } = useTour();

    useEffect(() => {
        if (isTablet || isMobile) {
            setIsOpened(false);
        }
    }, [language]);

    const onLogout = useCallback(() => {
        dispatch(invalidateSession({ isVoluntaryLogout: true }));
    }, [dispatch]);

    const handleLanguageChange = useCallback(
        async (newLanguage: Language) => {
            if (viewerId) {
                try {
                    await updateProfileData({
                        id: viewerId,
                        user_language: newLanguage,
                    });
                } catch (error) {
                    logError('Failed to update user language', error);
                }
            }
        },
        [viewerId, updateProfileData],
    );

    const dropdownRender = useCallback(
        (originNode: ReactNode) => (
            <HeaderMenuContent originNode={originNode} />
        ),
        [],
    );

    const onOpenChangeHandler = useCallback<
        Required<DropdownProps>['onOpenChange']
    >((nextOpen, info) => {
        if (info.source === 'trigger' || nextOpen) {
            setIsOpened(nextOpen);
        }
    }, []);

    const onMenuClick = useCallback<Required<MenuProps>['onClick']>((event) => {
        const keys = [route.getProfileManagementPath(), 'account&billing'];

        if ((isMobile || isTablet) && keys.includes(event.key)) {
            setIsOpened(false);
        }
    }, []);

    const signOutWithVersion = useCallback(
        () => (
            <div className={cls.signOut}>
                <span>{t('signOutButtonMenuItem')}</span>
                <span className={cls.version}>{packageJson.version}</span>
            </div>
        ),
        [t],
    );

    const hasTour = steps && steps.length > 0;

    const menu = useMemo<MenuProps>(
        () => ({
            items: [
                {
                    type: 'divider',
                } as Required<MenuProps>['items'][number],
                {
                    key: route.getProfileManagementPath(),
                    label: (
                        <Link to={route.getProfileManagementPath()}>
                            {t('profileManagementPageTitle')}
                        </Link>
                    ),
                },
                {
                    type: 'divider',
                } as Required<MenuProps>['items'][number],
                ...(hasTour
                    ? [
                          {
                              key: 'tour-trigger',
                              className: 'no-link',
                              label: <TourTriggerMenuItem />,
                          },
                      ]
                    : []),
                {
                    key: 'theme-switcher',
                    className: 'no-link',
                    label: <ThemeSwitcher isMenu />,
                },
                {
                    key: 'language-switcher',
                    className: 'no-link',
                    label: (
                        <LanguageSwitcher
                            isMenu
                            onLanguageChange={handleLanguageChange}
                        />
                    ),
                },
                {
                    type: 'divider',
                } as Required<MenuProps>['items'][number],
                {
                    key: 'sign-out',
                    label: signOutWithVersion(),
                    onClick: onLogout,
                },
            ],
            onClick: onMenuClick,
        }),
        [
            handleLanguageChange,
            hasTour,
            onLogout,
            onMenuClick,
            t,
            signOutWithVersion,
        ],
    );

    return (
        <Dropdown
            dropdownRender={dropdownRender}
            menu={menu}
            onOpenChange={onOpenChangeHandler}
            open={isOpened}
            rootClassName={cls.root}
            trigger={['click']}
        >
            <ViewerAvatar
                className={cls.trigger}
                data-tour="header-menu-account"
            />
        </Dropdown>
    );
});

HeaderMenuTrigger.displayName = 'HeaderMenuTrigger';
